import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { workOrderService } from '../../services/workOrderService';
import { customerService } from '../../services/customerService';
import { staffService } from '../../services/staffService';
import { pdfService } from '../../services/pdfService';
import LoadingSpinner from '../../components/LoadingSpinner';

// Import komponen-komponen kecil
import DetailHeader from './components/DetailHeader';
import DetailInformasi from './components/DetailInformasi';
import DetailTimeline from './components/DetailTimeline';
import DetailFabrikasi from './components/DetailFabrikasi';
import DetailMaterial from './components/DetailMaterial';
import DetailDokumen from './components/DetailDokumen';
import DetailSidebar from './components/DetailSidebar';

const WorkOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workOrder, setWorkOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [staff, setStaff] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [localProgress, setLocalProgress] = useState(0);
  const [isUpdatingProgress, setIsUpdatingProgress] = useState(false);

  useEffect(() => {
    if (workOrder) {
      setLocalProgress(workOrder.progress || 0);
    }
  }, [workOrder]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const wo = await workOrderService.getById(id);
      if (wo) {
        setWorkOrder(wo);
        const customerData = await customerService.getById(wo.customerId);
        setCustomer(customerData);
        const staffData = await staffService.getById(wo.staffId);
        setStaff(staffData);
      } else {
        setWorkOrder(null);
        setError('Work Order tidak ditemukan');
      }
    } catch (error) {
      console.error('Error loading work order:', error);
      setError('Gagal memuat data work order: ' + (error.message || ''));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleStatusChange = async (updatedWO) => {
    setWorkOrder(updatedWO);
    
    if (updatedWO.staffId) {
      try {
        const staffData = await staffService.getById(updatedWO.staffId);
        setStaff(staffData);
      } catch (error) {
        console.error('Error refreshing staff data:', error);
      }
    }
    
    if (updatedWO.customerId) {
      try {
        const customerData = await customerService.getById(updatedWO.customerId);
        setCustomer(customerData);
      } catch (error) {
        console.error('Error refreshing customer data:', error);
      }
    }
  };

  const handleUpdateProgress = async (val) => {
    if (val === (workOrder.progress || 0)) return;
    
    setIsUpdatingProgress(true);
    try {
      await workOrderService.updateProgress(
        workOrder.id, 
        val, 
        `Manual update: ${val}%`
      );
      await loadData();
    } catch (error) {
      console.error('Error updating progress:', error);
      alert('Gagal update progress: ' + error.message);
      setLocalProgress(workOrder.progress || 0);
    } finally {
      setIsUpdatingProgress(false);
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      const transaction = await workOrderService.getTransaction(workOrder.id);
      
      if (!transaction || transaction.length === 0) {
        alert('Transaksi belum ada. Pastikan WO sudah Completed.');
        return;
      }
      
      const tx = Array.isArray(transaction) ? transaction[0] : transaction;
      await pdfService.generateInvoice(workOrder, customer, tx);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Gagal download invoice: ' + error.message);
    }
  };

  const handleDownloadWorkOrder = async () => {
    try {
      await pdfService.generateWorkOrder(workOrder, customer, staff);
    } catch (error) {
      console.error('Error downloading work order:', error);
      alert('Gagal download work order: ' + error.message);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Memuat detail pekerjaan... ☕" />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[var(--text-secondary)]">Work Order tidak ditemukan</p>
      </div>
    );
  }

  return (
    <div>
      {/* HEADER */}
      <DetailHeader
        workOrder={workOrder}
        onBack={() => navigate('/work-orders')}
        onDownloadInvoice={handleDownloadInvoice}
        onDownloadWorkOrder={handleDownloadWorkOrder}
        onStatusChange={handleStatusChange}
      />

      {/* MAIN CONTENT - GRID 2 KOLOM */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

        {/* KOLOM KIRI (3/5) */}
        <div className="lg:col-span-3 space-y-4">
          <DetailInformasi
            workOrder={workOrder}
            localProgress={localProgress}
            setLocalProgress={setLocalProgress}
            isUpdatingProgress={isUpdatingProgress}
            onUpdateProgress={handleUpdateProgress}
          />

          <DetailTimeline workOrder={workOrder} />

          <DetailFabrikasi workOrder={workOrder} />

          <DetailMaterial workOrder={workOrder} />

          <DetailDokumen files={workOrder.files} />
        </div>

        {/* KOLOM KANAN (2/5) - SIDEBAR */}
        <DetailSidebar
          workOrder={workOrder}
          customer={customer}
          staff={staff}
        />
      </div>
    </div>
  );
};

export default WorkOrderDetail;